<script setup lang="ts">
import { ref } from 'vue';

import { AppButton } from '@/shared/ui/button';

withDefaults(
  defineProps<{
    isVisible: boolean;
    isForced?: boolean;
  }>(),
  {
    isForced: false,
  },
);

const emit = defineEmits<{
  reload: [];
  dismiss: [];
}>();

const isReloading = ref(false);

const handleReload = () => {
  isReloading.value = true;
  // Перезагрузку может отменить диалог «Покинуть сайт?» о несохранённых изменениях: не оставляем кнопку крутиться.
  setTimeout(() => (isReloading.value = false), 10_000);
  emit('reload');
};

const handleDismiss = () => {
  emit('dismiss');
};
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform translate-y-[-100%] opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform translate-y-[-100%] opacity-0"
  >
    <div
      v-if="isVisible"
      class="animate-border-pulse fixed top-4 right-4 left-4 z-(--toast-z-index) max-w-md rounded-lg border-2 border-(--accent) bg-(--surface-sunken) p-4 text-(--text-primary) shadow-lg sm:left-auto"
      style="
        box-shadow:
          0 0 20px color-mix(in srgb, var(--accent) 30%, transparent),
          0 0 40px color-mix(in srgb, var(--accent) 10%, transparent);
      "
    >
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <p class="leading-snug font-medium text-(--text-primary)">
            {{ isForced ? 'Критическое обновление' : 'Доступна новая версия' }}
          </p>
          <p class="text-sm leading-snug text-(--text-secondary)">
            {{
              isForced
                ? 'Обновите страницу: на старой версии работать нельзя'
                : 'Обновите страницу, чтобы получить последние изменения'
            }}
          </p>
        </div>
        <div class="flex items-center justify-end gap-2">
          <button
            v-if="!isForced"
            class="min-h-11 rounded-xl px-4 text-sm font-medium whitespace-nowrap text-(--text-secondary) transition-[background-color,transform] duration-200 hover:bg-(--surface-hover) active:scale-[0.96]"
            @click="handleDismiss"
          >
            Отложить
          </button>
          <AppButton
            class="min-h-11 font-medium whitespace-nowrap"
            :loading="isReloading"
            @click="handleReload"
          >
            <span>Обновить</span>
          </AppButton>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@keyframes border-pulse {
  0%,
  100% {
    box-shadow:
      0 0 20px color-mix(in srgb, var(--accent) 30%, transparent),
      0 0 40px color-mix(in srgb, var(--accent) 10%, transparent);
  }
  50% {
    box-shadow:
      0 0 30px color-mix(in srgb, var(--accent) 50%, transparent),
      0 0 60px color-mix(in srgb, var(--accent) 20%, transparent);
  }
}

.animate-border-pulse {
  animation: border-pulse 2s ease-in-out infinite;
}
</style>
